import React, {useEffect, useState} from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  FormControl
} from '@material-ui/core';
import {useTranslation} from 'react-i18next';
import {spaceBottomStyle} from './RecordDetailsDialogStyle';
import {Controller, useForm} from 'react-hook-form';
import StyledOutlinedInput from '@components/system/FormInput/StyledOutlinedInput';
import WithPermission from '@components/system/WithPermission/WithPermission';
import {AppPermissions, CCMConstants} from '@shared/constants/Mapping';
import {SnackBarConfig} from 'src/utils/SnackBarConfig';
import {useSnackbar} from 'notistack';
import {
  createCCMRecordLogDetails,
  getPatientCCMTaskDetails
} from '@shared/services/CCMServices';
import Storage from 'src/utils/Storage';
import ButtonLoader from '@components/system/ButtonLoader/ButtonLoader';
import Loader from '@components/framework/loader/Loader';

interface EnrollmentDialogProps {
  open: boolean;
  onClose: () => void;
  patientName: string;
  accountNumber: string;
  groupId: number;
  clinicId?: number | string;
  patientId: number | undefined;
  ccmPatientId: number;
  isExternalPlanActiveProp?: boolean;
}

const getDataEntryInputKey = (id: number) => {
  return `plan_log_${id}`;
};

const RecordDetailsDialog: React.FC<EnrollmentDialogProps> = ({
  open,
  onClose,
  patientName,
  accountNumber,
  groupId,
  clinicId,
  patientId,
  ccmPatientId,
  isExternalPlanActiveProp
}) => {
  const {t} = useTranslation();
  const {enqueueSnackbar} = useSnackbar();
  const {control, handleSubmit} = useForm({
    mode: 'all'
  });
  const [state, setState] = useState({
    planDataList: [],
    buttonLoader: false
  });
  const {planDataList, buttonLoader} = state;
  const [loader, setLoader] = useState(true);
  // getting the ccmPatientTimerLogId from local storage
  const ccmPatientTimerLogId: any = Storage.getItem(
    CCMConstants.STORAGE_CCM_TIMER_DATA
  );
  const timerLogId = JSON?.parse?.(ccmPatientTimerLogId || null);

  const renderDialogTitle = () => {
    return (
      <div>
        {t('CCM.RECORD_DETAILS.RECORD_DETAILS', {patientName: patientName})}
        <strong>&nbsp;&nbsp;&bull;&nbsp;&nbsp;</strong>
        {`${accountNumber})`}
      </div>
    );
  };

  const fetchCCMPlanTaskDetails = async () => {
    try {
      const response = await getPatientCCMTaskDetails(groupId, ccmPatientId, {
        clinic_id: clinicId,
        is_external_care_plan: isExternalPlanActiveProp
      });
      if (response) {
        setState(prevState => ({
          ...prevState,
          planDataList: response?.data?.rows
        }));
        setLoader(false);
      }
    } catch (err: any) {
      enqueueSnackbar(
        err.message || t('CCM.CCM_PLAN_TASK_DETAILS_FETCH_ERROR'),
        SnackBarConfig('e')
      );
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchCCMPlanTaskDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const renderContent = () => {
    return (
      <div>
        {!loader ? (
          <div style={{...spaceBottomStyle}}>
            {planDataList.map((item: any, i) => {
              return (
                <div style={{...spaceBottomStyle}} key={item.id}>
                  <div style={{marginBottom: '0.3rem'}}>
                    {item.task_type === CCMConstants.INFORMATIONAL ? (
                      <div>
                        {i + 1}
                        {`. ${item.task_name}`}
                      </div>
                    ) : (
                      <div>{`${i + 1}. ${item.task_name}`}</div>
                    )}
                  </div>
                  {item.task_type === CCMConstants.DATA_ENRTY && (
                    <FormControl
                      fullWidth={true}
                      variant="outlined"
                      style={{marginBottom: '-0.5rem'}}>
                      <Controller
                        id={`tasks.${i}.task_name`}
                        as={StyledOutlinedInput}
                        margin={'dense'}
                        control={control}
                        name={getDataEntryInputKey(item?.id)}
                        type={'text'}
                        value={'value'}
                        autoComplete="on"
                        placeholder={t('CCM.PLAN.ENTER_NOTES')}
                      />
                    </FormControl>
                  )}
                  <Divider />
                </div>
              );
            })}
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}>
            <Loader />
          </div>
        )}
      </div>
    );
  };

  const onSubmit = async (data: any) => {
    const payloadData: any = [];

    planDataList.map((item: any) => {
      const inputKey = getDataEntryInputKey(item.id);
      payloadData.push({
        ccm_patient_plan_id: item.id,
        response: data?.[inputKey] ?? null
      });
    });

    const payload = {
      plan_log: payloadData,
      patient_log_id: timerLogId.ccmPatientTimerLogId || 0,
      clinic_id: clinicId
    };
    const options: Record<string, any> = {};
    if (clinicId) {
      options.clinic_id = clinicId;
    }
    try {
      setState(prevState => ({
        ...prevState,
        buttonLoader: true
      }));

      const res = await createCCMRecordLogDetails(
        groupId,
        ccmPatientId,
        payload,
        options
      );
      if (res) {
        setState(prevState => ({
          ...prevState,
          buttonLoader: false
        }));
        enqueueSnackbar(
          t('CCM.API_SUCCESS_ERROR_MESSGAGES.UPDATED_RECORD_DEATILS_SUCCESS'),
          SnackBarConfig('s')
        );
        onClose();
      }
    } catch (err: any) {
      setState(prevState => ({
        ...prevState,
        buttonLoader: false
      }));
      enqueueSnackbar(err && err.message, SnackBarConfig('e'));
    }
  };

  const renderButtons = () => {
    return (
      <div>
        <Button onClick={onClose}>{t('COMMON.CANCEL')}</Button>
        <Button
          type="submit"
          variant={'contained'}
          onClick={handleSubmit(onSubmit)}
          color="primary">
          {buttonLoader ? <ButtonLoader /> : t('COMMON.SAVE')}
        </Button>
      </div>
    );
  };
  const renderDialogContent = () => {
    return <div>{renderContent()}</div>;
  };
  return (
    <Dialog
      disableEnforceFocus
      maxWidth={'sm'}
      fullWidth={true}
      disableBackdropClick={true}
      disableEscapeKeyDown={true}
      open={open}
      onClose={onClose}
      aria-labelledby="enrollment-dialog-title">
      <DialogTitle disableTypography id="enrollment-dialog-title">
        {renderDialogTitle()}
      </DialogTitle>
      <DialogContent dividers>{renderDialogContent()}</DialogContent>
      <DialogActions style={{display: 'flex'}}>{renderButtons()}</DialogActions>
    </Dialog>
  );
};
export default WithPermission(
  RecordDetailsDialog,
  AppPermissions.CCM.NAME,
  AppPermissions.CCM.READ
);
